// Presentation helpers for the Driver Roster grid: status-code styling, the
// legend, leave types and month/calendar maths. The roster itself is live data
// (see `useRoster`); this file no longer holds any driver fixtures.

export type StatusCode = 'W' | 'D/O' | 'H' | 'R' | 'S' | 'O';

export interface StatusDef {
  label: string;
  /** Cell classes (bg + text). */
  cell: string;
  /** Legend swatch classes. */
  dot: string;
}

export const STATUS: Record<StatusCode, StatusDef> = {
  W: { label: 'W', cell: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
  'D/O': { label: 'D/O', cell: 'text-slate-400', dot: 'border border-slate-300 bg-white' },
  H: { label: 'H', cell: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  R: { label: 'R', cell: 'bg-sky-100 text-sky-700', dot: 'bg-sky-400' },
  S: { label: 'S', cell: 'bg-red-100 text-red-700', dot: 'bg-red-400' },
  O: { label: 'O', cell: 'bg-violet-100 text-violet-700', dot: 'bg-violet-400' },
};

/** Legend entries, in display order. */
export const LEGEND: { code: StatusCode; label: string }[] = [
  { code: 'W', label: 'W — Working' },
  { code: 'D/O', label: 'D/O' },
  { code: 'H', label: 'Holidays' },
  { code: 'R', label: 'Rest Period' },
  { code: 'S', label: 'Sick Day' },
  { code: 'O', label: 'Other Leave' },
];

// ── Month ─────────────────────────────────────────────────────────────────--

export interface MonthInfo {
  label: string;
  days: number;
  /** 0 = Mon … 6 = Sun for day 1. */
  firstWeekday: number;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Build month info for a given year + 0-based month (real calendar maths). */
export function monthInfoFor(year: number, month0: number): MonthInfo {
  const days = new Date(year, month0 + 1, 0).getDate();
  const firstDow = new Date(year, month0, 1).getDay(); // 0 = Sun … 6 = Sat
  const firstWeekday = (firstDow + 6) % 7; // shift to 0 = Mon … 6 = Sun
  const label = new Date(year, month0, 1).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  });
  return { label, days, firstWeekday };
}

export function weekdayOf(month: MonthInfo, day: number): number {
  return (month.firstWeekday + day - 1) % 7;
}
export function weekdayLabel(month: MonthInfo, day: number): string {
  return WEEKDAYS[weekdayOf(month, day)]!;
}
export function isWeekend(month: MonthInfo, day: number): boolean {
  return weekdayOf(month, day) >= 5;
}
